import { useEffect, useRef, useState } from 'preact/hooks';
import type { Action, GameView, RulesConfig } from '../engine';
import { PING, type ClientMessage, type RoomSnapshot, type ServerMessage } from '../shared/protocol';
import type { LogEntry } from './game-client';
import { describeEvent } from './log';

const TOKENS_KEY = 'cyp.seats';
const PING_MS = 25_000;
const MAX_BACKOFF_MS = 10_000;
const LOG_LIMIT = 200;

export function roomSocketUrl(code: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/api/rooms/${code}/ws`;
}

function readTokens(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(TOKENS_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

export function seatToken(code: string): string | undefined {
  return readTokens()[code];
}

function storeToken(code: string, token: string | null): void {
  try {
    const tokens = readTokens();
    if (token) tokens[code] = token;
    else delete tokens[code];
    localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  } catch {
    // Private mode: the seat just won't survive closing the tab.
  }
}

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

/**
 * A room socket that reconnects with backoff and re-sends `hello` (with the
 * stored seat token) every time it opens. Phones drop sockets whenever the
 * browser is backgrounded, so reconnecting is the normal path, not an edge case.
 */
export class RoomConnection {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;

  constructor(
    private readonly code: string,
    private readonly onMessage: (msg: ServerMessage) => void,
    private readonly onStatus: (status: ConnectionStatus) => void,
  ) {
    document.addEventListener('visibilitychange', this.onVisible);
    this.open();
  }

  private onVisible = () => {
    if (document.visibilityState === 'visible' && !this.stopped && this.ws?.readyState !== WebSocket.OPEN) {
      this.attempt = 0;
      this.open();
    }
  };

  private open(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    this.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(roomSocketUrl(this.code));
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.attempt = 0;
      this.onStatus('open');
      const token = seatToken(this.code);
      this.send({ type: 'hello', ...(token ? { token } : {}) });
      this.pingTimer = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(PING), PING_MS);
    });
    ws.addEventListener('message', (e) => {
      if (typeof e.data !== 'string' || e.data === 'pong') return;
      const msg = JSON.parse(e.data) as ServerMessage;
      if (msg.type === 'welcome') {
        if (msg.token) storeToken(this.code, msg.token);
        else if (msg.seat === null) storeToken(this.code, null);
      }
      if (msg.type === 'error' && msg.fatal) this.stop();
      this.onMessage(msg);
    });
    ws.addEventListener('close', () => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stopped) return this.onStatus('closed');
      this.attempt++;
      this.onStatus('reconnecting');
      const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** (this.attempt - 1));
      this.retryTimer = setTimeout(() => this.open(), delay);
    });
  }

  send(msg: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  stop(): void {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.ws?.close(1000, 'leaving');
  }
}

export interface RoomState {
  status: ConnectionStatus;
  seat: number | null;
  room: RoomSnapshot | null;
  game: { view: GameView; legal: Action[] } | null;
  log: LogEntry[];
  /** Latest server error, with an id so repeated identical errors still show. */
  notice: { id: number; text: string } | null;
  /** Latest informational message (e.g. an undo), same id scheme as `notice`. */
  info: { id: number; text: string } | null;
  fatal: string | null;
}

export interface RoomControls {
  join(name: string): void;
  leave(): void;
  start(rules: Partial<RulesConfig>): void;
  addBot(): void;
  removeBot(seat: number): void;
  act(action: Action): void;
  undo(): void;
  release(seat: number, toBot?: boolean): void;
  claim(seat: number): void;
}

export function useRoom(code: string): RoomState & RoomControls {
  const [state, setState] = useState<RoomState>({
    status: 'connecting',
    seat: null,
    room: null,
    game: null,
    log: [],
    notice: null,
    info: null,
    fatal: null,
  });
  const conn = useRef<RoomConnection | null>(null);
  const ids = useRef(0);

  useEffect(() => {
    const onMessage = (msg: ServerMessage) =>
      setState((s) => {
        switch (msg.type) {
          case 'welcome':
            return { ...s, seat: msg.seat };
          case 'room':
            return { ...s, room: msg.room };
          case 'game': {
            const names = s.room?.seats.map((x) => x.name) ?? [];
            const lines = msg.events.flatMap((e) => {
              const text = describeEvent(e, names);
              return text ? [{ id: ++ids.current, text }] : [];
            });
            const log = (msg.reset ? lines : [...s.log, ...lines]).slice(-LOG_LIMIT);
            return { ...s, game: { view: msg.view, legal: msg.legal }, log };
          }
          case 'info':
            return { ...s, info: { id: ++ids.current, text: msg.message } };
          case 'error':
            return msg.fatal
              ? { ...s, fatal: msg.message }
              : { ...s, notice: { id: ++ids.current, text: msg.message } };
        }
      });
    const c = new RoomConnection(code, onMessage, (status) => setState((s) => ({ ...s, status })));
    conn.current = c;
    return () => c.stop();
  }, [code]);

  const send = (msg: ClientMessage) => {
    if (!conn.current?.send(msg)) {
      setState((s) => ({ ...s, notice: { id: ++ids.current, text: 'Not connected — try again in a moment.' } }));
    }
  };

  return {
    ...state,
    join: (name) => send({ type: 'join', name }),
    leave: () => send({ type: 'leave' }),
    start: (rules) => send({ type: 'start', rules }),
    addBot: () => send({ type: 'addBot' }),
    removeBot: (seat) => send({ type: 'removeBot', seat }),
    act: (action) => send({ type: 'action', action }),
    undo: () => send({ type: 'undo' }),
    release: (seat, toBot) => send({ type: 'release', seat, ...(toBot ? { toBot } : {}) }),
    claim: (seat) => send({ type: 'claim', seat }),
  };
}
